import { z } from 'zod';

/**
 * Cross-type message attribute fields — the ones that are not owned by any
 * single message type.
 *
 * A field belongs here only if more than one type can carry it, or if it
 * describes the message envelope rather than a type's payload. Anything a
 * single type owns lives in that type's module instead, so adding a field to
 * one type never touches this file.
 */

/**
 * Per-type behavior contract.
 *
 * Declared on the leaf module so a type can state its behavior without
 * importing the catalog that collects it.
 */
export type MessageBehavior = {
  /**
   * Whether the type drives the optimistic inbox unread badge and message
   * preview. Set `false` for a system message that only one participant can
   * see: the inbox handlers cannot tell the viewer's role apart (the author is
   * the system), so an optimistic write would show the other participant a
   * preview and unread badge for a message absent from their thread. The
   * backend owns role-correct unread/preview for those types.
   */
  drivesInboxPreview: boolean;
};

export const DEFAULT_MESSAGE_BEHAVIOR: MessageBehavior = {
  drivesInboxPreview: true,
};

export const filesSchema = z
  .array(
    z.object({
      s3Key: z.string(),
      fileName: z.string(),
    })
  )
  .optional();

/**
 * Backend-driven processing status surfaced to chat UI. Today this is
 * written by the inquiry-pipeline for expert answers (classify →
 * extract clinical statements → link to submitter questions →
 * complete), but the field is intentionally generic so future message
 * types that go through their own async processing can reuse it.
 *
 * The wire key on Twilio message attributes is `pipelineStatus` (legacy,
 * matches the only current producer). We normalize it to
 * `processingStatus` everywhere on the frontend.
 */
export const processingStatusSchema = z.enum(['reviewing', 'analyzing', 'linking', 'complete']);

export type ProcessingStatus = z.infer<typeof processingStatusSchema>;

const publicationShareAnchorSchema = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
  text: z.string(),
});

const publicationShareEntrySchema = z.object({
  shareId: z.string(),
  number: z.number().int().positive(),
  anchors: z.array(publicationShareAnchorSchema).catch([]),
  statementIndices: z.array(z.number().int().nonnegative()).catch([]),
});

/**
 * Summary of the publications shared in a message, written by the backend
 * after every share change. Absent when a message has no shares, so `count`
 * is never zero.
 *
 * `entries` is the message's reference list as the server numbered it: AMA
 * order, by first mention in the text or the end of the statement a work is
 * attached to, with a repeat keeping its first number. Numbers run 1 to
 * `count` with no gaps, and the app takes them as given rather than renumber.
 * Each entry's `anchors` are where the text cites it, as UTF-16 offsets into
 * the body with the text they should cover, so an anchor the body no longer
 * matches can be dropped. A message whose attribute predates entries parses
 * with none, and shows no references until the backend re-syncs it.
 *
 * `updatedAt` moves on any change to the list or its numbering, so it is the
 * cache key rather than `count`. `lastAddedAt` moves only when a share row is
 * added; it is the key for attributes written before `updatedAt` existed.
 *
 * `.catch(undefined)` keeps a malformed value from failing `safeParse` for the
 * whole attributes object, which would drop every field on the message.
 */
export const publicationSharesSchema = z
  .object({
    conversationMessageId: z.string().uuid(),
    count: z.number().int().positive(),
    lastAddedAt: z.string(),
    updatedAt: z.string().optional(),
    entries: z.array(publicationShareEntrySchema).catch([]),
  })
  .optional()
  .catch(undefined);

export type PublicationShares = NonNullable<z.infer<typeof publicationSharesSchema>>;

export type PublicationShareEntry = PublicationShares['entries'][number];

export type PublicationShareAnchor = PublicationShareEntry['anchors'][number];

export const baseAttributesSchema = z.object({
  files: filesSchema,
  type: z.string().optional(),
  /** When true, the message is a draft and is hidden from the conversation. */
  draft: z.boolean().optional(),
  /**
   * Set on a copy staff relayed from the other side of an Ask Anonymously case:
   * the sid of the message it was copied from. The server refuses to relay a
   * copy again, so the long-press menu offers no relay for one.
   */
  relayedFromMessageId: z.string().optional(),
  /**
   * The id the sender's app generated when Send was pressed. It is what lets a
   * pending message be matched to the stored one, whichever way it arrives.
   */
  clientMessageId: z.string().optional(),
  /**
   * `'suggested'` marks an expert-shared reference message. The retired
   * backend auto-attach pipeline tagged its messages `'auto'`; `.catch` is
   * deliberate so those historical values degrade to an absent source
   * instead of failing `safeParse` for the whole attributes object (which
   * would drop every field for that message).
   */
  source: z.literal('suggested').optional().catch(undefined),
  referenceStatus: z.enum(['pending', 'attached', 'none', 'failed']).optional(),
  referenceId: z.string().nullish(),
  referenceMessageId: z.string().nullish(),
  /**
   * Backend-driven async processing status. Wire key is `pipelineStatus`
   * (set by primum-infra `updateMessageStatus`); on the frontend we
   * expose this as `processingStatus` so it can be reused for other
   * message types that aren't pipeline-specific. Drives the
   * author-only `MessageProcessingProgress` bar below the message
   * until status is `complete`.
   */
  pipelineStatus: processingStatusSchema.optional(),
  /** ISO-8601 timestamp the most recent status transition was written. */
  pipelineStatusAt: z.string().optional(),
  publicationShares: publicationSharesSchema,
});
